import { BootstrapData, Category, GameEvent, Operation, Settings } from '../api/types';
import { hashStr, seasonCoins } from './coins';
import { computeDailyBudget } from './daily-budget';
import { recordedDaySet } from './recording-streak';

/**
 * Фаза 2 игрового контура: квест недели, сундук, босс месяца, финал сезона.
 * Всё детерминируется данными + леджером game_events; квест недели — pure
 * функция от (понедельник, категории), так что хранить его не нужно.
 */

// ---------------------------------------------------------------------------
// Календарь
// ---------------------------------------------------------------------------

export function toIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

export function monthKeyOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function prevMonthKey(now: Date = new Date()): string {
  return monthKeyOf(new Date(now.getFullYear(), now.getMonth() - 1, 1));
}

/** Понедельник недели данной даты (локально). */
export function weekMonday(now: Date = new Date()): string {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const shift = (d.getDay() + 6) % 7; // пн=0
  d.setDate(d.getDate() - shift);
  return toIso(d);
}

/** Все 7 дат недели по её понедельнику. */
export function weekDays(mondayIso: string): string[] {
  const m = mondayIso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return [];
  const out: string[] = [];
  for (let i = 0; i < 7; i++) {
    out.push(toIso(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + i)));
  }
  return out;
}

// ---------------------------------------------------------------------------
// Балансы: монеты сезона с учётом леджера, инвентарь заморозок
// ---------------------------------------------------------------------------

export function coinsBalance(data: BootstrapData, now: Date = new Date()): number {
  const month = monthKeyOf(now);
  const ledger = (data.game_events ?? [])
    .filter((e) => e.month === month)
    .reduce((s, e) => s + e.coins, 0);
  return seasonCoins(data.operations, now) + ledger;
}

function payloadOf(e: GameEvent): Record<string, unknown> {
  try {
    return e.payload ? (JSON.parse(e.payload) as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

/** Заморозки: куплены/выпали из сундука − использованы. За всё время леджера. */
export function freezeInventory(events: GameEvent[]): number {
  let n = 0;
  for (const e of events) {
    const f = Number(payloadOf(e).freeze ?? 0);
    if (Number.isFinite(f)) n += f;
  }
  return Math.max(0, n);
}

/** Даты, закрытые заморозкой (freeze_use.week = дата). */
export function frozenDates(events: GameEvent[]): Set<string> {
  const s = new Set<string>();
  for (const e of events) {
    if (e.type === 'freeze_use' && e.week) s.add(e.week);
  }
  return s;
}

export const FREEZE_PRICE = 150;

// ---------------------------------------------------------------------------
// Квест недели
// ---------------------------------------------------------------------------

export type Quest =
  | { kind: 'record_days'; target: number; reward: number; label: string }
  | { kind: 'no_days'; cat: string; icon: string; target: number; reward: number; label: string }
  | { kind: 'cap_week'; cat: string; icon: string; cap: number; reward: number; label: string };

export function generateQuest(categories: Category[], now: Date = new Date()): Quest {
  const wk = weekMonday(now);
  const pool: Quest[] = [
    { kind: 'record_days', target: 5, reward: 80, label: 'Запиши траты в 5 разных дней' },
  ];
  for (const c of categories) {
    if (c.type !== 'Расход' || !c.is_hot) continue;
    pool.push({
      kind: 'no_days',
      cat: c.name,
      icon: c.icon,
      target: 3,
      reward: 80,
      label: `3 дня без «${c.name}»`,
    });
    if (c.is_daily && c.plan_month > 0) {
      const cap = Math.max(500, Math.round(c.plan_month / 4.3 / 100) * 100);
      pool.push({
        kind: 'cap_week',
        cat: c.name,
        icon: c.icon,
        cap,
        reward: 100,
        label: `Не больше ${cap} ₽ на «${c.name}» за неделю`,
      });
    }
  }
  return pool[hashStr(wk + 'quest') % pool.length];
}

export interface QuestState {
  quest: Quest;
  /** 0..1 для прогресс-бара. */
  ratio: number;
  done: boolean;
  failed: boolean;
  detail: string;
}

export function questState(
  quest: Quest,
  operations: Operation[],
  now: Date = new Date(),
  mondayIso: string = weekMonday(now),
): QuestState {
  const days = weekDays(mondayIso);
  const today = toIso(now);
  const weekOver = today > days[6];

  if (quest.kind === 'record_days') {
    const recorded = recordedDaySet(operations);
    const n = days.filter((d) => recorded.has(d)).length;
    return {
      quest,
      ratio: Math.min(1, n / quest.target),
      done: n >= quest.target,
      failed: false,
      detail: `${n} из ${quest.target} дней`,
    };
  }

  if (quest.kind === 'no_days') {
    const spentDays = new Set(
      operations.filter((o) => o.category === quest.cat).map((o) => o.date),
    );
    // считаем только полностью прошедшие дни недели
    const passed = days.filter((d) => d < today);
    const clean = passed.filter((d) => !spentDays.has(d)).length;
    const daysAhead = days.filter((d) => d >= today).length;
    const failed = clean + daysAhead < quest.target;
    return {
      quest,
      ratio: Math.min(1, clean / quest.target),
      done: clean >= quest.target,
      failed,
      detail: failed ? 'на этой неделе уже не успеть' : `${clean} из ${quest.target} дней`,
    };
  }

  // cap_week
  const spent = operations
    .filter((o) => o.category === quest.cat && days.includes(o.date))
    .reduce((s, o) => s + o.amount, 0);
  const failed = spent > quest.cap;
  return {
    quest,
    ratio: Math.min(1, spent / quest.cap),
    done: weekOver && !failed,
    failed,
    detail: failed
      ? `потрачено ${Math.round(spent)} — лимит пробит`
      : `потрачено ${Math.round(spent)} из ${quest.cap}`,
  };
}

// ---------------------------------------------------------------------------
// Сундук недели
// ---------------------------------------------------------------------------

export interface Chest {
  weekMonday: string;
  coins: number;
  freeze: boolean;
  lines: string[];
}

/** Содержимое сундука за неделю — детерминировано данными недели. */
export function chestFor(
  mondayIso: string,
  operations: Operation[],
  categories: Category[],
  settings: Settings | null,
): Chest {
  const days = weekDays(mondayIso);
  const recorded = recordedDaySet(operations);
  const recordedDays = days.filter((d) => recorded.has(d)).length;

  const dailyBudget = computeDailyBudget(categories, settings);
  const catByName = new Map(categories.map((c) => [c.name, c] as const));
  const spentByDay = new Map<string, number>();
  for (const op of operations) {
    const cat = catByName.get(op.category);
    const t = cat?.type ?? op.type;
    if (t !== 'Расход') continue;
    if (cat && !cat.is_daily) continue;
    spentByDay.set(op.date, (spentByDay.get(op.date) || 0) + op.amount);
  }
  const underDays = days.filter((d) => recorded.has(d) && (spentByDay.get(d) || 0) <= dailyBudget).length;

  const lines: string[] = [];
  let coins = recordedDays * 10;
  if (recordedDays > 0) lines.push(`записи: ${recordedDays} дн × 10 = ${recordedDays * 10}`);
  coins += underDays * 10;
  if (underDays > 0) lines.push(`дни в лимите: ${underDays} × 10 = ${underDays * 10}`);

  // квест оцениваем «после конца недели» (пн+7), чтобы cap_week-квесты засчитались
  const mm = mondayIso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const evalDate = mm
    ? new Date(Number(mm[1]), Number(mm[2]) - 1, Number(mm[3]) + 7, 12)
    : new Date();
  // ручной локальный парс: new Date('YYYY-MM-DD') дал бы UTC-полночь,
  // при отрицательной TZ понедельник уехал бы на воскресенье
  const qDate = mm ? new Date(Number(mm[1]), Number(mm[2]) - 1, Number(mm[3]), 12) : new Date();
  const q = generateQuest(categories, qDate);
  const qs = questState(q, operations, evalDate, mondayIso);
  if (qs.done) {
    coins += q.reward;
    lines.push(`квест «${q.label}»: +${q.reward}`);
  }

  const freeze = recordedDays === 7;
  if (freeze) lines.push('идеальная неделя: заморозка');

  return { weekMonday: mondayIso, coins, freeze, lines };
}

/**
 * Сундук, доступный к открытию: текущая неделя с воскресенья, прошлая — если
 * не открыта; грейс до 4 недель назад (самый старый непустой первым) — чтобы
 * вернувшегося после паузы встречала награда, а не пустота.
 */
export function claimableChest(
  data: BootstrapData,
  now: Date = new Date(),
): { monday: string } | null {
  const opened = new Set(
    (data.game_events ?? []).filter((e) => e.type === 'chest_open').map((e) => e.week),
  );
  const curMonday = weekMonday(now);
  const weekShift = (now.getDay() + 6) % 7;

  for (let back = 4; back >= 1; back--) {
    const monday = toIso(
      new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7 * back - weekShift),
    );
    if (opened.has(monday)) continue;
    // прошлая неделя — всегда; более старые — только если сундук непустой
    if (back === 1) return { monday };
    const contents = chestFor(monday, data.operations, data.categories, data.settings);
    if (contents.coins > 0 || contents.freeze) return { monday };
  }

  const isSunday = now.getDay() === 0;
  if (isSunday && !opened.has(curMonday)) {
    return { monday: curMonday };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Босс месяца
// ---------------------------------------------------------------------------

export interface Boss {
  cat: string;
  icon: string;
  /** HP = план месяца. */
  hp: number;
  spent: number;
  hpLeft: number;
  alive: boolean;
  /** Откуда выбран: 'prev' — перерасход прошлого месяца, 'cur' — темп текущего. */
  source: 'prev' | 'cur';
}

export const BOSS_REWARD = 200;

export function pickBoss(
  operations: Operation[],
  categories: Category[],
  settings: Settings | null,
  now: Date = new Date(),
): Boss | null {
  const curKey = monthKeyOf(now);
  const prevKey = prevMonthKey(now);
  const since = settings?.stats_since ?? null;

  const spentBy = (monthKey: string) => {
    const m = new Map<string, number>();
    for (const op of operations) {
      if (!op.date.startsWith(monthKey)) continue;
      if (since && op.date < since) continue;
      m.set(op.category, (m.get(op.category) || 0) + op.amount);
    }
    return m;
  };

  const candidates = categories.filter(
    (c) => c.type === 'Расход' && c.is_daily && c.plan_month > 0,
  );
  if (candidates.length === 0) return null;

  // приоритет: худший перерасход прошлого месяца
  const prevSpent = spentBy(prevKey);
  let best: { c: Category; over: number } | null = null;
  for (const c of candidates) {
    const over = (prevSpent.get(c.name) || 0) - c.plan_month;
    if (over > 0 && (!best || over > best.over)) best = { c, over };
  }
  let source: Boss['source'] = 'prev';

  // fallback: самый горячий темп текущего месяца
  if (!best) {
    source = 'cur';
    const curSpent = spentBy(curKey);
    let worstRatio = 0;
    for (const c of candidates) {
      const ratio = (curSpent.get(c.name) || 0) / c.plan_month;
      if (ratio > worstRatio) {
        worstRatio = ratio;
        best = { c, over: 0 };
      }
    }
    if (!best || worstRatio === 0) return null;
  }

  const spent = spentBy(curKey).get(best.c.name) || 0;
  const hp = best.c.plan_month;
  return {
    cat: best.c.name,
    icon: best.c.icon,
    hp,
    spent,
    hpLeft: hp - spent,
    alive: spent <= hp,
    source,
  };
}

// ---------------------------------------------------------------------------
// Финал сезона
// ---------------------------------------------------------------------------

export interface SeasonSummary {
  month: string;
  income: number;
  expense: number;
  saved: number;
  coins: number;
  tickets: number;
  bossWin: boolean | null;
  bossCat: string | null;
}

export function summarizeSeason(data: BootstrapData, monthKey: string): SeasonSummary {
  const since = data.settings.stats_since ?? null;
  const catType = new Map(data.categories.map((c) => [c.name, c.type] as const));
  let income = 0;
  let expense = 0;
  for (const op of data.operations) {
    if (!op.date.startsWith(monthKey)) continue;
    if (since && op.date < since) continue;
    const t = catType.get(op.category) ?? op.type;
    if (t === 'Доход') income += op.amount;
    else if (t === 'Расход') expense += op.amount;
  }

  const refDate = new Date(`${monthKey}-15T12:00:00`);
  const ledger = (data.game_events ?? [])
    .filter((e) => e.month === monthKey && e.type !== 'season_close')
    .reduce((s, e) => s + e.coins, 0);
  const coins = seasonCoins(data.operations, refDate) + ledger;

  // итог босса прошлого месяца: пересобираем его состояние на конец месяца
  const boss = pickBoss(
    data.operations,
    data.categories,
    data.settings,
    refDate,
  );

  return {
    month: monthKey,
    income,
    expense,
    saved: income - expense,
    coins,
    tickets: Math.max(0, Math.floor(coins / 100)),
    bossWin: boss ? boss.alive : null,
    bossCat: boss?.cat ?? null,
  };
}

/**
 * Финал: незакрытый сезон показывается ВЕСЬ месяц (пропуск ничего не
 * отнимает — GAME_LAYER §1); карточка-итог закрытого — первые 7 дней.
 */
export function seasonFinaleState(
  data: BootstrapData,
  now: Date = new Date(),
): { month: string; closed: GameEvent | null } | null {
  const prevKey = prevMonthKey(now);
  const closed =
    (data.game_events ?? []).find((e) => e.type === 'season_close' && e.week === prevKey) ?? null;
  const day = now.getDate();
  if (!closed) {
    // есть ли вообще что закрывать
    const hasOps = data.operations.some((o) => o.date.startsWith(prevKey));
    const since = data.settings.stats_since ?? null;
    if (hasOps && (!since || `${prevKey}-28` >= since)) return { month: prevKey, closed: null };
    return null;
  }
  if (day <= 7) return { month: prevKey, closed };
  return null;
}
